'use client';

import { useState } from 'react';
import { getRedisCacheKeysAction } from '@/operations/flushRedisAction';
import { QuickLinks } from '@/components/QuickLinks';
import ErrorBoundary from '@/components/ErrorBoundary';
import Error from '@/components/Error';
import { Suspense } from 'react';
import SubmitButton from '@/components/SubmitButton';
import { Table, Alert, Select, Button } from 'design-system/client';
import styles from './GetRedisCacheKeys.module.css';
import getIsProduction from '@/helpers/utils/getIsProduction';
type RedisKeyRow = {
  key: string;
  id: string;
};
function useKeysTableData(keys: string[]): Array<RedisKeyRow> {
  return keys.map(key => ({
    key,
    id: key
  }));
}
function GetRedisCacheKeys() {
  const [keys, setKeys] = useState<string[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [selectedPrefix, setSelectedPrefix] = useState<string | undefined>(undefined);
  const [hasFetched, setHasFetched] = useState(false);
  const data = useKeysTableData(keys);
  const columns: Array<{
    label: string;
    field: keyof RedisKeyRow;
  }> = [{
    label: 'Key',
    field: 'key'
  }];
  const getRowId = (row: RedisKeyRow) => row.id;
  if (getIsProduction() && process.env.REDIS_AVAILABLE !== '1') return null;
  const handleClear = () => {
    setKeys([]);
    setHasFetched(false);
    setSelectedPrefix(undefined);
  };
  const handleGetKeys = async () => {
    setLoading(true);
    setError(null);
    try {
      const formData = new FormData();
      if (selectedPrefix) {
        formData.set('prefix', selectedPrefix);
      }
      const result = await getRedisCacheKeysAction(formData);
      setHasFetched(true);
      if (result?.success && result.keys) {
        setKeys(result.keys);
      } else {
        setKeys([]);
      }
    } catch (e) {
      const message = e && typeof e === 'object' && 'message' in e ? e.message as string : 'An error occurred while fetching Redis keys.';
      setError(message);
    } finally {
      setLoading(false);
    }
  };
  return <QuickLinks id="flush-redis-cache">
      <ErrorBoundary errorComponent={Error}>
        <Suspense fallback={null}>
          <div>
            <div className={styles.controlsRow}>
              <Select id="prefix-select" label="Prefix" value={selectedPrefix} onChange={e_0 => setSelectedPrefix(e_0.target.value || undefined)}>
                <option value="">All keys</option>
                <option value="next">FE keys</option>
              </Select>
              <SubmitButton kind="secondary" onClick={() => void handleGetKeys()} loadingContent={loading}>
                Get Redis Keys
              </SubmitButton>
            </div>
            {hasFetched && keys.length && <div className={styles.counterWrapper}>
                <span className={styles.counter}>{keys.length} keys</span>
                <Button kind="plain" size="small" onClick={handleClear}>
                  Clear
                </Button>
              </div>}
            {error && <Alert kind="error" style={{
            textAlign: 'center'
          }}>
                {error}
              </Alert>}
            {hasFetched && keys.length === 0 && <Alert kind="warning" style={{
            textAlign: 'center'
          }}>
                No cache keys found for the selected prefix.
              </Alert>}
            {keys.length > 0 && <div className={styles.tableWrapper}>
                <Table className={styles.table} id="redis-keys-table" columns={columns} data={data} getRowId={getRowId} pivot={false} scrollToEndLabel="" />
              </div>}
          </div>
        </Suspense>
      </ErrorBoundary>
    </QuickLinks>;
}
export default GetRedisCacheKeys;